/**
 * @file LoadMoreButton.jsx
 * @description Sayfalama ve daha fazla yükle butonu
 *
 * İlan listesinin altında gösterilen sayfalama kontrolleri.
 * "Daha Fazla Yükle" ve "Tüm İlanlara Git" butonları içerir.
 *
 * @module widget/components/LoadMoreButton
 */

import { ChevronDown, ExternalLink, Loader2 } from "lucide-react";
import { openExternal, formatNumber } from "../utils";

/**
 * Sayfalama Buton Bileşeni
 *
 * İlan listesi sonunda gösterilen sayfalama kontrolleri.
 * İki buton içerir: infinite scroll için "Daha Fazla Yükle"
 * ve Emlakjet web sitesine yönlendirme için "Tüm İlanlara Git".
 *
 * @component
 * @param {Object} props - Bileşen prop'ları
 * @param {number} props.total - Toplam ilan sayısı
 * @param {number} props.currentCount - Şu an gösterilen ilan sayısı
 * @param {boolean} props.isLoading - Yükleme durumu
 * @param {Function} props.onLoadMore - Daha fazla yükle callback'i
 * @param {Object} props.query - Mevcut arama sorgusu
 * @param {string} [props.query.path] - Arama yolu
 * @param {string} [props.query.filters] - Filtre string'i
 * @param {boolean} props.isDark - Karanlık mod
 *
 * @returns {JSX.Element} Sayfalama butonları
 *
 * @example
 * <LoadMoreButton
 *   total={150}
 *   currentCount={20}
 *   isLoading={false}
 *   onLoadMore={() => loadNextPage()}
 *   query={{ path: "satilik-konut/istanbul", filters: "oda-sayisi=3-1" }}
 *   isDark={false}
 * />
 */
export function LoadMoreButton({
  total,
  currentCount,
  isLoading,
  onLoadMore,
  query,
  isDark,
}) {
  const handleViewAll = () => {
    const emlakjetUrl = `https://www.emlakjet.com${
      query?.path ? `/${query.path}` : "/satilik-konut"
    }${query?.filters ? `?filtreler=${encodeURIComponent(query.filters)}` : ""}`;
    openExternal(emlakjetUrl);
  };

  return (
    <div
      className={`p-4 flex flex-col gap-2 border-t ${
        isDark ? "border-white/10" : "border-black/10"
      }`}
    >
      {/* Load More */}
      <button
        onClick={onLoadMore}
        disabled={isLoading}
        className={`w-full py-2.5 px-4 border border-emerald-600 rounded-lg bg-transparent text-emerald-600 text-sm font-medium flex items-center justify-center gap-2 transition-opacity ${
          isLoading
            ? "opacity-50 cursor-not-allowed"
            : "cursor-pointer hover:bg-emerald-600/5"
        }`}
      >
        {isLoading ? (
          <>
            <Loader2 className="h-4 w-4 animate-spin" strokeWidth={1.5} />
            Yükleniyor...
          </>
        ) : (
          <>
            <ChevronDown className="h-4 w-4" strokeWidth={1.5} />
            Daha Fazla Yükle ({currentCount} / {formatNumber(total)})
          </>
        )}
      </button>

      {/* View All on Emlakjet */}
      <button
        onClick={handleViewAll}
        className="w-full py-2.5 px-4 border border-emerald-600 rounded-lg bg-transparent text-emerald-600 text-sm font-medium flex items-center justify-center gap-2 cursor-pointer hover:bg-emerald-600/5 transition-colors"
      >
        <ExternalLink className="h-4 w-4" strokeWidth={1.5} />
        Tüm İlanlara Git
      </button>
    </div>
  );
}

export default LoadMoreButton;
